/*LIBRARY*/
import { Component } from "react";
import { connect, Provider } from "react-redux";
import bbox from "@turf/bbox";
import maplibregl from "maplibre-gl";
import centroid from "@turf/centroid";
import along from "@turf/along";
import calculate_length from "@turf/length";
import ReactDOM from "react-dom";

/*COMPONENT*/
import POPUP_SMALL from "../libre_popup/POPUP_SMALL";
import A_ARROW_PERPENDICULAR from "../layer_anotation/A_ARROW_PERPENDICULAR";
import Modal from "../../Components/common_modal/Modal";

/*REDUX*/
import { set_value_layer } from "../../App/actions/layerActions";
import {
  set_value_genangan,
  genangan_update,
} from "../../App/actions/genangan_actions";
import { set_value_properties } from "../../App/actions/propertiesActions";

/*FUNCTION*/
import generate_color_map from "../../App/validation/generate_color_map";
import store from "../../Debug/store";

/*CONST*/
const convert_multi_point_to_points = (geojson) => {
  const features = geojson.features.flatMap((feature) => {
    if (feature.geometry.type === "MultiPoint") {
      return feature.geometry.coordinates.map((coord) => ({
        type: "Feature",
        geometry: {
          type: "Point",
          coordinates: coord,
        },
        properties: feature.properties,
      }));
    }
    return feature;
  });
  return {
    type: "FeatureCollection",
    features,
  };
};

class LAYER_GEO extends Component {
  state = {
    popup_handlers: {},
  };

  componentDidUpdate(prevProps) {
    const status_action_after = this.props.map.status_action;
    const status_action_before = prevProps.map.status_action;

    const basemap_used_after = this.props.properties.basemap_used;
    const basemap_used_before = prevProps.properties.basemap_used;

    const map_object_string = this.props.map_object_string || "map_object";
    const map_object = this.props.layer[map_object_string];

    // Added checks for data changes (geo_layer_list) and active layers
    const data_changed =
      this.props.layer.geo_layer_list !== prevProps.layer.geo_layer_list;
    const active_layer_changed =
      this.props.layer.layer_id_active !== prevProps.layer.layer_id_active;

    if (
      status_action_after !== status_action_before ||
      basemap_used_after !== basemap_used_before ||
      data_changed ||
      active_layer_changed
    ) {
      if (map_object && map_object.isStyleLoaded()) {
        this.on_render();
        this.setup_global_popup_handler();
      } else if (map_object) {
        map_object.once("styledata", () => {
          this.on_render();
          this.setup_global_popup_handler();
        });
      }
    }

    const fly_action_after = this.props.map.fly_action;
    const fly_action_before = prevProps.map.fly_action;
    if (fly_action_after !== fly_action_before) {
      this.on_fly();
    }
  }

  componentWillUnmount() {
    this.cleanup_event_listeners();
  }

  on_fly = async () => {
    const {
      geo_layer_list,
      layer_id,
      is_pause_zoom_map,
      max_zoom,
      padding_fly,
    } = this.props.layer;
    const map_object_string = this.props.map_object_string || "map_object";
    const map_object = this.props.layer[map_object_string];
    try {
      if (!is_pause_zoom_map) {
        const geo_layer = geo_layer_list.find(
          (item) => item?.geo_layer?._id === layer_id
        )?.geo_layer;
        const geojson_filtered = geo_layer?.geojson_filtered;
        const features = geojson_filtered?.features || [];
        if (features?.length > 0 && !!map_object) {
          const [min_longitude, min_latitude, max_longitude, max_latitude] =
            bbox(geojson_filtered);
          map_object.fitBounds(
            [
              [min_longitude, min_latitude],
              [max_longitude, max_latitude],
            ],
            {
              padding: padding_fly,
              maxZoom: max_zoom,
              duration: 750,
            }
          );
        }
      }
    } catch (error) {
      console.error("Error during map 'on_fly' operation:", error);
    }
  };

  on_render = async () => {
    try {
      let annotation_jsx_list = []; // Reset list at start of render

      const key_after =
        this.props.layer.feature_object_selected?.properties?.key || "";
      const {
        geo_layer_list,
        layer_apply_color,
        layer_id,
        layer_id_active,
        geometryStatus,
      } = this.props.layer;
      const { is_preview_on, paint_object_edited } = this.props.properties;

      const map_object_string = this.props.map_object_string || "map_object";
      const map_object = this.props.layer[map_object_string];

      if (!map_object) {
        throw new Error("Map object is not available");
      }

      if (!geo_layer_list || !Array.isArray(geo_layer_list)) {
        throw new Error("geo_layer_list is invalid or not an array");
      }

      for (const layer of geo_layer_list) {
        try {
          const _id = layer?.geo_layer?._id;

          if (!_id) {
            console.warn("Layer missing _id, skipping:", layer);
            continue;
          }

          const geo_layer = layer?.geo_layer || {};
          const type_2 = layer?.geo_layer?.type_2;

          // =========================================================
          // 1. ANNOTATION RENDERING LOGIC
          // =========================================================
          if (type_2 === "annotation") {
            // Visibility Check
            if (!layer_id_active.includes(_id) || geometryStatus) {
              continue;
            }

            const fields = layer?.geo_layer?.fields || [];
            const features = layer?.geo_layer?.geojson?.features || [];

            // Dictionary for dynamic field mapping
            const field_map = fields.reduce((acc, item) => {
              acc[item.name] = item.key;
              return acc;
            }, {});

            const layer_annotations = features.map((feature_item, index) => {
              const properties = feature_item.properties;
              const geometry = feature_item.geometry;
              const coordinates = geometry?.coordinates || [];

              // Extract values
              const title_val = properties[field_map["title"]] || "";
              const desc_val = properties[field_map["desc"]] || "";
              const width_val = Number(properties[field_map["width"]] || 0);
              const height_val = Number(properties[field_map["height"]] || 0);
              const image_1 = properties[field_map["image_1"]] || "";
              const image_2 = properties[field_map["image_2"]] || "";

              // Positioning & Styling Fields
              const arrow_w_val = Number(
                properties[field_map["arrow_width"]] || 0.001
              );
              const arrow_h_val = Number(
                properties[field_map["arrow_height"]] || 0.001
              );
              const box_pos_val =
                properties[field_map["box_position"]] || "top_right";

              // NEW: Get arrow style from properties
              const arrow_style_val =
                properties[field_map["arrow_style"]] || "triangle";

              let anchor_long, anchor_lat;

              // Determine Anchor Point based on Geometry Type
              if (geometry.type === "LineString" && coordinates.length > 0) {
                // LineString: Use the first coordinate as start
                anchor_long = coordinates[0][0];
                anchor_lat = coordinates[0][1];
              } else if (geometry.type === "Point" && coordinates.length >= 2) {
                // Point: Use the coordinates directly
                anchor_long = coordinates[0];
                anchor_lat = coordinates[1];
              } else {
                return null; // Invalid geometry for annotation
              }

              const unique_key = feature_item._id || `${_id}_${index}`;

              return (
                <A_ARROW_PERPENDICULAR
                  key={unique_key}
                  // Content
                  title={title_val}
                  desc={desc_val}
                  image_1={image_1}
                  image_2={image_2}
                  // Anchor Location
                  long={anchor_long}
                  lat={anchor_lat}
                  // Box & Arrow Configuration
                  long_width={width_val} // Box Width (Degrees)
                  lat_width={height_val} // Box Aspect Ratio Helper (Degrees)
                  arrow_width={arrow_w_val} // Arrow Gap Scale
                  arrow_height={arrow_h_val} // Arrow Gap Scale
                  box_position={box_pos_val} // Position relative to anchor
                  // Styling
                  corner_mode="horizontal-first"
                  arrow_style={arrow_style_val}
                />
              );
            });

            if (layer_annotations.length > 0) {
              annotation_jsx_list.push(...layer_annotations.filter(Boolean));
            }

            // Skip standard mapbox layer logic for annotations
            continue;
          }

          // =========================================================
          // 2. STANDARD MAPBOX LAYER LOGIC (Unchanged)
          // =========================================================
          else {
            const { is_use_symbol } = geo_layer;
            const features_filtered =
              layer?.geo_layer?.geojson_filtered?.features || [];
            const type = layer?.geo_layer?.type;

            const default_style_key = layer?.geo_layer?.default_style_key;
            const style_array = layer?.geo_layer?.style_array || [];
            let properties_raw = layer?.geo_layer?.properties;
            const is_cluster = layer?.geo_layer?.is_cluster;

            let shape_type;
            let color_key;
            let paint_object = {};
            let paint_object_line_polygon = {};
            let visibility = layer_id_active.includes(_id) ? "visible" : "none";

            if (is_use_symbol && (type === "Point" || type === "IoT")) {
              visibility = "none";
            }
            if (geometryStatus && layer_id === _id) visibility = "none";

            const apply_color_id = layer_apply_color.find(
              (item) => item.layer_id === layer.geo_layer?._id
            );

            const geojson = {
              type: "FeatureCollection",
              features: features_filtered,
            };

            let geojson_point;
            switch (type) {
              case "Point":
              case "MultiPoint":
                shape_type = "circle";
                color_key = "circle-color";
                paint_object = {
                  "circle-color": "#fff",
                  "circle-opacity": 1,
                  "circle-radius": 5,
                  "circle-stroke-color": "#000",
                  "circle-stroke-width": 2,
                  "circle-stroke-opacity": 1,
                  "circle-blur": 0,
                  "circle-pitch-alignment": "map",
                  "circle-pitch-scale": "map",
                  "circle-translate": [0, 0],
                  "circle-translate-anchor": "map",
                };
                break;
              case "LineString":
              case "MultiLineString":
                shape_type = "line";
                color_key = "line-color";
                paint_object = {
                  "line-color": "#000",
                  "line-width": 5,
                  "line-opacity": 1,
                };
                break;
              case "Polygon":
              case "MultiPolygon":
                shape_type = "fill";
                color_key = "fill-color";
                paint_object = {
                  "fill-color": "#1a649d",
                  "fill-opacity": 0.7,
                };
                paint_object_line_polygon = {
                  "line-color": "#000",
                  "line-width": 1,
                  "line-opacity": 1,
                  "line-dasharray": [1, 0],
                };
                break;
              default:
                shape_type = "circle";
                color_key = "circle-color";
                paint_object = {
                  "circle-color": "#1a649d",
                  "circle-opacity": 1,
                  "circle-radius": 3,
                  "circle-stroke-color": "#fff",
                  "circle-stroke-width": 2,
                  "circle-stroke-opacity": 1,
                  "circle-blur": 0,
                  "circle-pitch-alignment": "map",
                  "circle-pitch-scale": "map",
                  "circle-translate": [0, 0],
                  "circle-translate-anchor": "map",
                };
                break;
            }

            const is_error = !!properties_raw?.[0]?.["LineString"];
            if (is_error) {
              if (type === "Point" || type === "MultiPoint") {
                properties_raw = properties_raw?.[0]?.["Point"];
              } else if (type === "LineString" || type === "MultiLineString") {
                properties_raw = properties_raw?.[0]?.["LineString"];
              } else if (type === "Polygon" || type === "MultiPolygon") {
                properties_raw = properties_raw?.[0]?.["Polygon"];
              }
            }

            let circle_radius = Number(
              properties_raw?.find((d) => d?.key === "circle_radius")
                ?.defaultValue
            );
            let opacity = Number(
              properties_raw?.find((d) => d?.key === "opacity")?.defaultValue
            );
            let outline;
            let stroke;
            let case_color_style = 0;

            if (apply_color_id) {
              case_color_style = 1;
              let apply_value =
                apply_color_id?.config?.array_color?.labels || [];
              apply_value = apply_value.map((item) => item);
              let apply_color =
                apply_color_id?.config?.array_color?.datasets?.[0]
                  ?.backgroundColor;
              const array_apply_color = generate_color_map(
                apply_value,
                apply_color,
                apply_color_id?.config?.field_key
              );

              if (type === "Point" || type === "MultiPoint") {
                paint_object = {
                  "circle-color": array_apply_color,
                  "circle-radius": circle_radius ? circle_radius : 7,
                  "circle-stroke-width": stroke ? stroke : 2,
                  "circle-stroke-color": outline ? outline : "#fff",
                };
              } else if (type === "LineString" || type === "MultiLineString") {
                paint_object = {
                  "line-color": array_apply_color,
                  "line-width": 3,
                  "line-opacity": opacity ? opacity : 0.5,
                };
              } else if (type === "Polygon" || type === "MultiPolygon") {
                paint_object = {
                  "fill-color": array_apply_color,
                  "fill-opacity": 0.4,
                  "fill-outline-color": "transparent",
                };
              }
            } else if (
              is_preview_on &&
              !!paint_object_edited?.[color_key] &&
              layer_id === _id
            ) {
              case_color_style = 2;
              if (shape_type === "fill") {
                paint_object = {
                  "fill-color": paint_object_edited?.["fill-color"],
                  "fill-opacity": paint_object_edited?.["fill-opacity"],
                  "fill-outline-color": "transparent",
                };
                paint_object_line_polygon = {
                  "line-color": paint_object_edited?.["line-color"],
                  "line-width": paint_object_edited?.["line-width"],
                  "line-opacity": paint_object_edited?.["line-opacity"],
                };
              } else if (shape_type === "line") {
                paint_object = {
                  "line-color": paint_object_edited?.["line-color"],
                  "line-width": paint_object_edited?.["line-width"],
                  "line-opacity": paint_object_edited?.["line-opacity"],
                };
              } else {
                paint_object = paint_object_edited;
              }
            } else if (style_array.length > 0) {
              case_color_style = 3;
              const paint_object_db =
                style_array.find((item) => item?.key === default_style_key)
                  ?.paint_object || {};

              if (shape_type === "fill") {
                paint_object = {
                  "fill-color": paint_object_db["fill-color"],
                  "fill-opacity": paint_object_db["fill-opacity"],
                  "fill-outline-color": "transparent",
                };
                paint_object_line_polygon = {
                  "line-color": paint_object_db["line-color"],
                  "line-width": paint_object_db["line-width"],
                  "line-opacity": paint_object_db["line-opacity"],
                };
              } else if (shape_type === "line") {
                paint_object = {
                  "line-color": paint_object_db["line-color"],
                  "line-width": paint_object_db["line-width"],
                  "line-opacity": paint_object_db["line-opacity"],
                };
              } else {
                const paint_object_baru = { ...paint_object_db };
                paint_object_baru["circle-stroke-color"] = [
                  "match",
                  ["get", "key"],
                  key_after,
                  "rgb(0,0,0)",
                  paint_object_db["circle-stroke-color"],
                ];

                paint_object_baru["circle-stroke-width"] = key_after
                  ? 4
                  : paint_object_db["circle-stroke-width"];

                paint_object = paint_object_baru;
              }
            }

            layer.case_color_style = case_color_style;
            const cluster_id = _id + "_cluster";

            try {
              if (!map_object.getSource(_id)) {
                map_object.addSource(_id, {
                  type: "geojson",
                  data: geojson,
                  cluster: false,
                });

                if (type === "Point" || type === "MultiPoint") {
                  const cluster_data =
                    type === "MultiPoint"
                      ? convert_multi_point_to_points(geojson)
                      : geojson;

                  if (!map_object.getSource(cluster_id)) {
                    map_object.addSource(cluster_id, {
                      type: "geojson",
                      data: cluster_data,
                      cluster: true,
                      clusterMaxZoom: 14,
                      clusterRadius: 50,
                    });
                  }
                }
              } else {
                map_object.getSource(_id).setData(geojson);

                if (type === "Point") {
                  if (map_object.getSource(cluster_id)) {
                    map_object.getSource(cluster_id).setData(geojson);
                  } else {
                    map_object.addSource(cluster_id, {
                      type: "geojson",
                      data: geojson,
                      cluster: true,
                      clusterMaxZoom: 14,
                      clusterRadius: 50,
                    });
                  }
                } else if (type === "MultiPoint") {
                  geojson_point = convert_multi_point_to_points(geojson);
                  if (map_object.getSource(cluster_id)) {
                    map_object.getSource(cluster_id).setData(geojson_point);
                  } else {
                    map_object.addSource(cluster_id, {
                      type: "geojson",
                      data: geojson_point,
                      cluster: true,
                      clusterMaxZoom: 14,
                      clusterRadius: 50,
                    });
                  }
                }
              }
            } catch (sourceError) {
              console.error(
                `Failed to add/update source for layer ${_id}:`,
                sourceError
              );
              throw new Error(
                `Source operation failed for layer ${_id}: ${sourceError.message}`
              );
            }

            try {
              if (!map_object.getLayer(_id)) {
                if (shape_type === "fill") {
                  map_object.addLayer({
                    id: _id,
                    source: _id,
                    type: shape_type,
                    paint: paint_object,
                    layout: {
                      visibility: visibility,
                    },
                  });
                  map_object.addLayer({
                    id: _id + "_line_polygon",
                    source: _id,
                    type: "line",
                    paint: paint_object_line_polygon,
                    layout: {
                      visibility: visibility,
                    },
                  });
                } else if (is_cluster && shape_type === "circle") {
                  map_object.addLayer({
                    id: _id,
                    type: "circle",
                    source: cluster_id,
                    filter: ["!", ["has", "point_count"]],
                    paint: paint_object,
                    layout: {
                      visibility: visibility,
                    },
                  });
                  map_object.addLayer({
                    id: _id + "_clusters_container",
                    type: "circle",
                    source: cluster_id,
                    filter: ["has", "point_count"],
                    paint: {
                      "circle-color": [
                        "step",
                        ["get", "point_count"],
                        "#51bbd6",
                        100,
                        "#f1f075",
                        750,
                        "#f28cb1",
                      ],
                      "circle-radius": [
                        "step",
                        ["get", "point_count"],
                        20,
                        100,
                        30,
                        750,
                        40,
                      ],
                    },
                    layout: {
                      visibility: visibility,
                    },
                  });
                  map_object.addLayer({
                    id: _id + "_cluster_count_number",
                    type: "symbol",
                    source: cluster_id,
                    filter: ["has", "point_count"],
                    layout: {
                      "text-field": "{point_count_abbreviated}",
                      "text-size": 12,
                      visibility: visibility,
                    },
                    paint: {
                      "text-color": "#ffffff",
                    },
                  });
                } else {
                  map_object.addLayer({
                    id: _id,
                    source: _id,
                    type: shape_type,
                    paint: paint_object,
                    layout: {
                      visibility: visibility,
                    },
                  });
                }
              } else {
                if (map_object.getLayer(_id)) {
                  map_object.setLayoutProperty(_id, "visibility", visibility);
                }

                if (is_cluster && shape_type === "circle") {
                  const currentSource = map_object.getLayer(_id).source;
                  if (currentSource !== cluster_id) {
                    map_object.removeLayer(_id);
                    map_object.addLayer({
                      id: _id,
                      type: "circle",
                      source: cluster_id,
                      filter: ["!", ["has", "point_count"]],
                      paint: paint_object,
                      layout: {
                        visibility: visibility,
                      },
                    });
                  }

                  if (!map_object.getLayer(_id + "_clusters_container")) {
                    map_object.addLayer({
                      id: _id + "_clusters_container",
                      type: "circle",
                      source: cluster_id,
                      filter: ["has", "point_count"],
                      paint: {
                        "circle-color": [
                          "step",
                          ["get", "point_count"],
                          "#51bbd6",
                          100,
                          "#f1f075",
                          750,
                          "#f28cb1",
                        ],
                        "circle-radius": [
                          "step",
                          ["get", "point_count"],
                          20,
                          100,
                          30,
                          750,
                          40,
                        ],
                      },
                      layout: {
                        visibility: visibility,
                      },
                    });
                  } else {
                    map_object.setLayoutProperty(
                      _id + "_clusters_container",
                      "visibility",
                      visibility
                    );
                  }

                  if (!map_object.getLayer(_id + "_cluster_count_number")) {
                    map_object.addLayer({
                      id: _id + "_cluster_count_number",
                      type: "symbol",
                      source: cluster_id,
                      filter: ["has", "point_count"],
                      layout: {
                        "text-field": "{point_count_abbreviated}",
                        "text-size": 12,
                        visibility: visibility,
                      },
                      paint: {
                        "text-color": "#ffffff",
                      },
                    });
                  } else {
                    map_object.setLayoutProperty(
                      _id + "_cluster_count_number",
                      "visibility",
                      visibility
                    );
                  }
                } else if (!is_cluster && shape_type === "circle") {
                  const currentSource = map_object.getLayer(_id).source;
                  if (currentSource === cluster_id) {
                    map_object.removeLayer(_id);
                    if (map_object.getLayer(_id + "_clusters_container")) {
                      map_object.removeLayer(_id + "_clusters_container");
                    }
                    if (map_object.getLayer(_id + "_cluster_count_number")) {
                      map_object.removeLayer(_id + "_cluster_count_number");
                    }
                    map_object.addLayer({
                      id: _id,
                      type: "circle",
                      source: _id,
                      paint: paint_object,
                      layout: {
                        visibility: visibility,
                      },
                    });
                  }
                } else if (shape_type === "fill") {
                  if (map_object.getLayer(_id + "_line_polygon")) {
                    map_object.setLayoutProperty(
                      _id + "_line_polygon",
                      "visibility",
                      visibility
                    );
                  }
                }

                for (const property in paint_object) {
                  if (map_object.getLayer(_id)) {
                    map_object.setPaintProperty(
                      _id,
                      property,
                      paint_object[property]
                    );
                  }
                }

                if (
                  shape_type === "fill" &&
                  map_object.getLayer(_id + "_line_polygon")
                ) {
                  for (const property in paint_object_line_polygon) {
                    map_object.setPaintProperty(
                      _id + "_line_polygon",
                      property,
                      paint_object_line_polygon[property]
                    );
                  }
                }
              }
            } catch (layerError) {
              console.error(`Failed to add/update layer ${_id}:`, layerError);
              throw new Error(
                `Layer operation failed for layer ${_id}: ${layerError.message}`
              );
            }
          }
        } catch (layerError) {
          console.error(
            `Error processing layer ${layer?.geo_layer?._id || "unknown"}:`,
            layerError
          );
        }
      }

      // Dispatch the accumulated annotations to Redux once at the end
      this.props.set_value_layer({
        key: "annotation_jsx_list",
        value: annotation_jsx_list,
      });
    } catch (error) {
      console.error("Critical error in on_render:", error);
    }
  };

  setup_global_popup_handler = () => {
    const map_object_string = this.props.map_object_string || "map_object";
    const map_object = this.props.layer[map_object_string];
    const { geo_layer_list, geometryStatus } = this.props.layer;

    if (!map_object || geometryStatus) return;

    map_object.off("click", this.handle_map_click);
    map_object.off("touchstart", this.handle_touch_start);
    map_object.off("touchend", this.handle_touch_end);
    map_object.off("touchmove", this.handle_touch_move);

    // Filter out annotations from click events
    this.layer_ids = geo_layer_list
      .filter((layer) => layer?.geo_layer?.type_2 !== "annotation")
      .map((layer) => layer?.geo_layer?._id)
      .filter(Boolean);

    map_object.on("click", this.handle_map_click);
    map_object.on("touchstart", this.handle_touch_start);
    map_object.on("touchend", this.handle_touch_end);
    map_object.on("touchmove", this.handle_touch_move);
  };

  handle_touch_start = (e) => {
    this.touch_moved = false;
    this.touch_start_point = e.point;
  };

  handle_touch_move = (e) => {
    if (!this.touch_start_point) return;
    const dx = Math.abs(e.point.x - this.touch_start_point.x);
    const dy = Math.abs(e.point.y - this.touch_start_point.y);
    const distance = Math.sqrt(dx * dx + dy * dy);
    if (distance > 5) {
      this.touch_moved = true;
    }
  };

  handle_touch_end = (e) => {
    if (!this.touch_moved) {
      this.handle_map_click(e);
    }
    this.touch_moved = false;
    this.touch_start_point = null;
  };

  cleanup_event_listeners = () => {
    const { popup_handlers } = this.props.layer;
    const map_object_string = this.props.map_object_string || "map_object";
    const map_object = this.props.layer[map_object_string];
    if (map_object) {
      if (popup_handlers) {
        Object.keys(popup_handlers).forEach((layer_id) => {
          map_object.off("click", layer_id, popup_handlers[layer_id]);
          map_object.off("touchstart", layer_id, popup_handlers[layer_id]);
        });
        this.props.set_value_layer({
          key: "popup_handlers",
          value: {},
        });
      }
      map_object.off("click", this.handle_map_click);
      map_object.off("touchstart", this.handle_touch_start);
      map_object.off("touchend", this.handle_touch_end);
      map_object.off("touchmove", this.handle_touch_move);
    }
  };

  handle_map_click = (e) => {
    const { layer } = this.props;
    const map_object_string = this.props.map_object_string || "map_object";
    const map_object = layer[map_object_string];
    const { geo_layer_list, geometryStatus } = layer;
    if (!map_object || geometryStatus) return;
    const features = map_object.queryRenderedFeatures(e.point, {
      layers: this.layer_ids,
    });
    if (!features.length) return;
    const top_feature = features[0];
    const layer_id = top_feature.layer.id;
    const layer_found = geo_layer_list.find(
      (l) => l?.geo_layer?._id === layer_id
    );
    if (!layer_found) return;

    const { geo_layer } = layer_found;
    const feature_key = top_feature.properties?.key || top_feature.id;
    const feature_object_selected = geo_layer?.geojson?.features?.find(
      (item) => item?.key === feature_key
    );
    if (!feature_object_selected) return;
    const current_popup = this.props.layer.popup_dom;
    if (current_popup) current_popup.remove();
    this.generate_single_popup(feature_object_selected, geo_layer);
  };

  generate_single_popup = (feature, geo_layer) => {
    this.props.set_value_layer({
      key: "feature_object_selected",
      value: feature,
    });
    const type_2 = geo_layer?.side_table_object?.type_2;
    const fields = geo_layer?.fields || [];
    const geometry = feature?.geometry;
    const type = geometry?.type;
    let longitude, latitude;
    if (type === "Point") {
      [longitude, latitude] = geometry.coordinates;
    } else if (type === "LineString") {
      const length_km = calculate_length(feature, { units: "kilometers" });
      const center = along(feature, length_km / 2, { units: "kilometers" });
      [longitude, latitude] = center.geometry.coordinates;
    } else {
      const center = centroid({
        type: "FeatureCollection",
        features: [feature],
      });
      [longitude, latitude] = center.geometry.coordinates;
    }
    const container = document.createElement("div");
    const handleClose = () => {
      popup.remove();
      this.props.set_value_layer({ key: "popup_dom", value: null });
    };

    const handleFullScreen = () => {
      this.props.set_value_layer({
        key: "modal_edit_feature_properties",
        value: true,
      });
      this.props.set_value_layer({ key: "latitude_selected", value: latitude });
      this.props.set_value_layer({
        key: "longitude_selected",
        value: longitude,
      });
      this.props.set_value_layer({ key: "fields_selected", value: fields });
      this.props.set_value_layer({
        key: "properties_selected",
        value: feature.properties,
      });
      this.props.set_value_layer({ key: "type_2_selected", value: type_2 });
      this.props.set_value_layer({
        key: "feature_key_selected",
        value: feature.properties?.key,
      });
      this.props.set_value_layer({
        key: "geo_layer_selected",
        value: geo_layer,
      });
    };

    const handleBanjirGenerate = () => {
      this.props.set_value_properties({
        key: "modal_flood_new",
        value: true,
      });
      this.props.set_value_genangan({
        key: "geojson_flood_in_progress",
        value: {
          type: "FeatureCollection",
          features: [],
        },
      });
      this.props.set_value_genangan({
        key: "current_dem",
        value: 0,
      });
      this.props.set_value_genangan({
        key: "current_calc",
        value: 0,
      });
    };
    ReactDOM.render(
      <Provider store={store}>
        <POPUP_SMALL
          geo_layer_id={geo_layer?._id}
          feature={feature}
          fields={fields}
          type_2={type_2}
          onClose={handleClose}
          handleFullScreen={handleFullScreen}
          onBanjirGenerate={handleBanjirGenerate}
        />
      </Provider>,
      container
    );
    const popup = new maplibregl.Popup({ closeButton: false })
      .setLngLat([longitude, latitude])
      .setDOMContent(container)
      .addTo(this.props.layer[this.props.map_object_string || "map_object"]);
    this.props.set_value_layer({ key: "popup_dom", value: popup });
    container.parentElement.style.borderRadius = "15px";
    container.parentElement.style.margin = "0";
    container.parentElement.style.padding = "10px";
    container.parentElement.style.overflow = "hidden";
  };

  close_annotation_modal = () => {
    this.props.set_value_layer({
      key: "modal_annotation_data",
      value: null,
    });
  };

  render() {
    const { annotation_jsx_list, modal_annotation_data } = this.props.layer;
    return (
      <main>
        {annotation_jsx_list}
        {modal_annotation_data && (
          <Modal
            isOpen={true}
            onClose={this.close_annotation_modal}
            modalSize="medium"
          >
            <div className="box-body" id="box-body">
              <h2
                style={{
                  marginTop: 0,
                  marginBottom: "20px",
                  fontSize: "24px",
                  color: "#222",
                }}
              >
                {modal_annotation_data.title}
              </h2>

              {modal_annotation_data.image_1 && (
                <div style={{ marginBottom: "20px" }}>
                  <img
                    src={modal_annotation_data.image_1}
                    alt="Primary View"
                    style={{
                      width: "100%",
                      height: "auto",
                      borderRadius: "8px",
                      objectFit: "cover",
                      border: "1px solid #eee",
                    }}
                  />
                </div>
              )}

              {modal_annotation_data.desc && (
                <p
                  style={{
                    fontSize: "16px",
                    lineHeight: "1.6",
                    color: "#444",
                    marginBottom: "20px",
                    whiteSpace: "pre-wrap",
                  }}
                >
                  {modal_annotation_data.desc}
                </p>
              )}

              {modal_annotation_data.image_2 && (
                <div style={{ marginTop: "10px" }}>
                  <img
                    src={modal_annotation_data.image_2}
                    alt="Secondary View"
                    style={{
                      width: "100%",
                      height: "auto",
                      borderRadius: "8px",
                      objectFit: "cover",
                      border: "1px solid #eee",
                    }}
                  />
                </div>
              )}
            </div>
          </Modal>
        )}
      </main>
    );
  }
}

const mapStateToProps = (state) => ({
  map: state.map,
  layer: state.layer,
  properties: state.properties,
});

export default connect(mapStateToProps, {
  set_value_layer,
  set_value_properties,
  set_value_genangan,
  genangan_update,
})(LAYER_GEO);
